import {forEachOfArray} from "@acng/frontend-bounty/array.js";
import {TAGNAME_BODY} from "@acng/frontend-bounty/dom/type.js";
import {body} from "@acng/frontend-bounty";

import {CTX_OBSERVE, CTX_SUBSCRIBE_TAGNAME} from "../minify.js";

/**
 * @template {readonly import("../model/context-interface").Context<any>[]} T
 *
 * @param {string} signature
 * [CSS selector](https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_selectors)
 *
 * @param {T} contexts
 *
 * @returns {<X, E extends Element>(element: E, handler: (
 *   element: E,
 *   value: {[P in keyof T]: T[P] extends import("../model/context-interface").Context<infer V> ? V : never},
 *   previous: {[P in keyof T]: T[P] extends import("../model/context-interface").Context<infer V> ? V | undefined : never}
 * ) => X) => X}
 */
export function WatchGroup(signature, ...contexts) {
  const symbol = Symbol();

  for (const context of contexts) {
    context[CTX_SUBSCRIBE_TAGNAME](signature, symbol);
  }

  return (host, callback) => {
    let enablePropagation = false;

    /**
     * @type {any}
     */
    const allCurrent = [];

    /**
     * @type {any}
     */
    const allPrevious = [];

    // It might be good to be able to replace the listener without looking up tha context again
    // then this could be two loops
    // 1. CTX_OBSERVE to get the current value
    // 2. CTX_REPLACE the real listener (enablePropagation)
    // a.t.m. two CTX_OBSERVE would be possible but expensive.
    forEachOfArray(contexts, (ctx, i) =>
      ctx[CTX_OBSERVE](
        host,
        (current) => {
          if (enablePropagation) {
            forEachOfArray(allCurrent, (v, j) => (allPrevious[j] = v));
            allCurrent[i] = current;
            callback(host, allCurrent, allPrevious);
          } else {
            allCurrent[i] = current;
          }
        },
        symbol
      )
    );

    enablePropagation = true;

    return callback(host, allCurrent, allPrevious);
  };
}

/**
 * @template {readonly import("../model/context-interface").Context<any>[]} T
 *
 * @param {T} contexts
 *
 * @returns {<X>(handler: (
 *   value: {[P in keyof T]: T[P] extends import("../model/context-interface").Context<infer V> ? V : never},
 *   previous: {[P in keyof T]: T[P] extends import("../model/context-interface").Context<infer V> ? V | undefined : never}
 * ) => X) => X}
 */
export function GlobalWatchGroup(...contexts) {
  const watch = WatchGroup(TAGNAME_BODY, ...contexts);

  return (handler) => watch(body, (_, value, previous) => handler(value, previous));
}
